import EditIcon from '@/components/icons/EditIcon';
import QuizForm from '@/components/pages/create-webinar/live/quiz/QuizForm';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type {
  QuizAnswerOption,
  QuizAnswerOptionInput,
  QuizQuestionInput,
  QuizTemplate,
  QuizTemplateInput,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Loader2, Play, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';
import { useTranslation } from 'react-i18next';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import type { LiveSessionResourceStateDto, TriggerEventInput } from '@/features/live/types';
import {
  createQuizAnswerOption,
  deleteQuizAnswerOption,
  deleteQuizTemplate,
  fetchQuizTemplate,
  listQuizTemplates,
  updateQuizAnswerOption,
  updateQuizQuestion,
  updateQuizTemplate,
} from '@/features/webinar/webinarThunks';
import { fetchQuizResults } from '@/features/live/liveThunks';
import QuizCard from '@/components/common/asset/QuizCard';
import type { RootState } from '@/app/store';

export default function QuizsList({
  quizs,
  visualIdentity,
  workspaceId,
  webinarId,
  sessionId,
  adminResourceStates,
  handleTriggerEvent,
  liveParticipantCountSync,
}: {
  quizs: QuizTemplate[];
  visualIdentity: WorkspaceVisualIdentity;
  workspaceId: number;
  webinarId: number;
  sessionId?: number;
  adminResourceStates: LiveSessionResourceStateDto[];
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  liveParticipantCountSync?: number;
}) {
  const dispatch = useAppDispatch();
  const quizResultsByTemplateId = useAppSelector(
    (s: RootState) => s.live.quizResultsByTemplateId ?? {},
  );
  const { t: tCommon } = useTranslation('common');
  const { t: tWebinars } = useTranslation('webinars');
  const [selectedQuiz, setSelectedQuiz] = useState<QuizTemplate | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loadingQuizId, setLoadingQuizId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleEditQuiz = (quiz: QuizTemplate) => {
    setSelectedQuiz(quiz);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedQuiz(null);
  };

  const getColorValueById = (
    colorId: string | undefined,
    customColor: string | undefined,
    defaultColor: string,
    visualIdentity: WorkspaceVisualIdentity | null | undefined,
  ): string => {
    if (!colorId || colorId === 'custom') {
      return customColor || defaultColor;
    }

    if (/^#[0-9A-Fa-f]{6}$/.test(colorId)) {
      return colorId;
    }

    if (visualIdentity) {
      if (colorId === 'accent') {
        return visualIdentity.accentColor || defaultColor;
      }
      if (colorId === 'background') {
        return visualIdentity.backgroundColor || defaultColor;
      }
      if (colorId === 'contrast') {
        return visualIdentity.contrastColor || defaultColor;
      }
      if (colorId === 'secondary-background') {
        return visualIdentity.secondaryBackgroundColor || defaultColor;
      }
      if (colorId === 'gradient1-color1') {
        return 'gradient1';
      }
      if (colorId === 'gradient2-color1') {
        return 'gradient2';
      }
    }

    if (colorId === 'black') {
      return '#000000';
    }
    if (colorId === 'white') {
      return '#FFFFFF';
    }

    return defaultColor;
  };

  const resolveColorValue = (
    value?: string,
    custom?: string | null,
    visualIdentity?: WorkspaceVisualIdentity | null,
  ) => {
    if (!value) return null;
    if (value === 'custom') {
      return custom ?? null;
    }
    if (value === 'no-border') {
      return null;
    }

    return getColorValueById(value, custom ?? undefined, '#000000', visualIdentity);
  };

  const handleQuizSubmit = async (quizData: any) => {
    if (!selectedQuiz?.id) {
      console.error('No quiz selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const templateId = selectedQuiz.id;

      const backgroundColor = resolveColorValue(
        quizData.backgroundColor,
        quizData.customBackgroundColor,
        visualIdentity,
      );
      const borderColor = resolveColorValue(
        quizData.borderColor,
        quizData.customBorderColor,
        visualIdentity,
      );
      const accentColor = resolveColorValue(
        quizData.accentColor,
        quizData.customAccentColor,
        visualIdentity,
      );
      const titleColor = quizData.titleColor || '#000000';

      const quizTemplateInput: QuizTemplateInput = {
        name: quizData.name,
        enabledDuringLive: quizData.enabledDuringLive ?? false,
        shuffleQuestions: quizData.shuffleQuestions ?? null,
        shuffleOptions: quizData.shuffleOptions ?? null,
        passingScore: quizData.passingScore ?? null,
        visibleFrom: quizData.visibleFrom ?? null,
        visibleUntil: quizData.visibleUntil ?? null,
        published: quizData.published ?? null,
        backgroundColor: backgroundColor || null,
        borderColor: borderColor,
        accentColor: accentColor || null,
        titleColor: titleColor,
      };

      await dispatch(
        updateQuizTemplate({
          workspaceId,
          webinarId,
          templateId,
          input: quizTemplateInput,
        }),
      ).unwrap();

      const currentQuiz = await dispatch(
        fetchQuizTemplate({
          workspaceId,
          webinarId,
          templateId,
        }),
      ).unwrap();

      const existingQuestion = currentQuiz?.questions?.[0];
      if (existingQuestion?.id) {
        const questionInput: QuizQuestionInput = {
          textContent: quizData.question.textContent,
          type: quizData.question.type,
          position: quizData.question.position ?? 0,
          points: quizData.question.points ?? null,
          timeLimitSeconds: quizData.question.timeLimitSeconds ?? null,
        };

        await dispatch(
          updateQuizQuestion({
            workspaceId,
            webinarId,
            templateId,
            questionId: existingQuestion.id,
            input: questionInput,
          }),
        ).unwrap();
      }

      const existingServerOptions = currentQuiz?.questions?.[0]?.options || [];
      const existingOptionMap = new Map<number, QuizAnswerOption>(
        existingServerOptions.map((opt) => [opt.id, opt]),
      );
      const existingOptionTextMap = new Map<string, QuizAnswerOption>(
        existingServerOptions.map((opt) => [opt.textContent.trim().toLowerCase(), opt]),
      );

      const processedOptionIds = new Set<number>();
      const processedOptionTexts = new Set<string>();
      const questionId = existingQuestion?.id;

      if (!questionId) {
        console.error('Question ID not found');
        return;
      }

      for (const option of quizData.question.options || []) {
        if (!option.textContent?.trim()) {
          continue;
        }

        const normalizedText = option.textContent.trim().toLowerCase();

        if (option.id && processedOptionIds.has(option.id)) {
          processedOptionTexts.add(normalizedText);
          continue;
        }

        if (processedOptionTexts.has(normalizedText)) {
          continue;
        }

        const trimmedTextContent = option.textContent.trim();
        const calculatedPosition = option.position ?? processedOptionIds.size;

        if (option.id && existingOptionMap.has(option.id)) {
          const serverOption = existingOptionMap.get(option.id)!;
          const textContentChanged = serverOption.textContent.trim() !== trimmedTextContent;
          const positionChanged = serverOption.position !== calculatedPosition;
          const correctChanged = serverOption.correct !== (option.correct ?? false);

          if (textContentChanged || positionChanged || correctChanged) {
            const optionInput: QuizAnswerOptionInput = {
              textContent: trimmedTextContent,
              position: calculatedPosition,
              correct: option.correct ?? false,
            };

            await dispatch(
              updateQuizAnswerOption({
                workspaceId,
                webinarId,
                templateId,
                questionId,
                optionId: option.id,
                input: optionInput,
              }),
            ).unwrap();
          }
          processedOptionIds.add(option.id);
          processedOptionTexts.add(normalizedText);
        } else if (!option.id) {
          const existingByText = existingOptionTextMap.get(normalizedText);

          if (existingByText) {
            processedOptionIds.add(existingByText.id);
            processedOptionTexts.add(normalizedText);
          } else {
            const optionInput: QuizAnswerOptionInput = {
              textContent: trimmedTextContent,
              position: calculatedPosition,
              correct: option.correct ?? false,
            };

            const optionId = await dispatch(
              createQuizAnswerOption({
                workspaceId,
                webinarId,
                templateId,
                questionId,
                input: optionInput,
              }),
            ).unwrap();

            if (optionId) {
              processedOptionIds.add(optionId);
              processedOptionTexts.add(normalizedText);
            }
          }
        }
      }

      for (const serverOption of existingServerOptions) {
        if (!processedOptionIds.has(serverOption.id)) {
          await dispatch(
            deleteQuizAnswerOption({
              workspaceId,
              webinarId,
              templateId,
              questionId,
              optionId: serverOption.id,
            }),
          ).unwrap();
        }
      }

      const updatedQuiz = await dispatch(
        fetchQuizTemplate({
          workspaceId,
          webinarId,
          templateId,
        }),
      ).unwrap();

      if (handleTriggerEvent) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_UPDATE',
          resourceType: 'QUIZ_TEMPLATE',
          resourceId: updatedQuiz.id,
        });
      }

      setSelectedQuiz(updatedQuiz);
      toast.success(tWebinars('adminLive.editModal.quizSaved'));
    } catch (error) {
      console.error('Error updating quiz:', error);

      const appError = error as AppError | undefined;

      if (appError?.message) {
        const mainMessage = tCommon(appError.message, appError.message);
        toast.error(mainMessage);
      } else if (error instanceof Error && error.message) {
        toast.error(tCommon('errors.generic', error.message));
      } else {
        toast.error(tCommon('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
      }

      if (appError?.details && Array.isArray(appError.details)) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedMessage = tCommon(mappedMessage, detail.message);
            toast.error(translatedMessage);
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuiz = async () => {
    if (!selectedQuiz?.id) {
      console.error('No quiz selected');
      return;
    }

    const confirmed = window.confirm(tWebinars('adminLive.editModal.quizDeleteConfirm'));

    if (!confirmed) {
      return;
    }

    try {
      await dispatch(
        deleteQuizTemplate({
          workspaceId,
          webinarId,
          templateId: selectedQuiz.id,
        }),
      ).unwrap();

      await dispatch(listQuizTemplates({ workspaceId, webinarId })).unwrap();

      setSelectedQuiz(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error deleting quiz:', error);
    }
  };

  const handleSaveFromModal = () => {
    if ((window as any).quizFormSave) {
      (window as any).quizFormSave();
    }
  };

  const handleToggleQuizLive = async (quiz: QuizTemplate) => {
    if (!handleTriggerEvent) return;

    const isQuizLive = adminResourceStates.some(
      (state) =>
        state.resourceType === 'QUIZ_TEMPLATE' && state.resourceId === quiz.id && state.live,
    );

    setLoadingQuizId(quiz.id);
    try {
      if (isQuizLive) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_HIDE',
          resourceType: 'QUIZ_TEMPLATE',
          resourceId: quiz.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
        if (workspaceId && webinarId) {
          setTimeout(() => {
            dispatch(
              fetchQuizResults({
                workspaceId,
                webinarId,
                templateId: quiz.id,
                sessionId,
              }),
            ).catch(() => {
              /* ignorer les erreurs (ex: API non disponible) */
            });
          }, 0);
        }
      } else {
        await handleTriggerEvent({
          eventType: 'RESOURCE_SHOW',
          resourceType: 'QUIZ_TEMPLATE',
          resourceId: quiz.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
      }
    } catch (error) {
      console.error('Error toggling quiz live:', error);
    } finally {
      setLoadingQuizId(null);
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      style={{ color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000' }}
    >
      {quizs
        ?.filter((quiz) => !quiz.waitingRoomQuiz)
        .map((quiz) => {
          const isQuizLive = adminResourceStates.some(
            (state) =>
              state.resourceType === 'QUIZ_TEMPLATE' && state.resourceId === quiz.id && state.live,
          );

          return (
            <div key={quiz.id} className="w-full">
              <p className="text-sm font-medium pb-4">{quiz.name}</p>
              <div className=" flex  gap-[10px] ">
                {/* question */}
                <QuizCard
                  quiz={quiz}
                  visualIdentity={visualIdentity}
                  isAdmin
                  webinarId={webinarId}
                  isShow={isQuizLive}
                  showResults={!!quizResultsByTemplateId[quiz.id]}
                  quizResults={quizResultsByTemplateId[quiz.id] ?? undefined}
                  liveParticipantCountSync={liveParticipantCountSync}
                />

                {/* settings */}
                <div className="flex flex-col gap-4 ">
                  <button
                    className="rounded-lg p-3 cursor-pointer"
                    onClick={() => handleToggleQuizLive(quiz)}
                    disabled={loadingQuizId === quiz.id}
                    style={{
                      backgroundColor: isQuizLive
                        ? '#FFD753'
                        : visualIdentity?.contrastColor || '#2356CC',
                    }}
                  >
                    {loadingQuizId === quiz.id ? (
                      <Loader2
                        size={18}
                        color={isQuizLive ? '#000000' : '#FFFFFF'}
                        className="animate-spin"
                      />
                    ) : isQuizLive ? (
                      <X size={18} color="#000000" />
                    ) : (
                      <Play size={18} color="#FFFFFF" />
                    )}
                  </button>
                  <button
                    className="rounded-lg p-3 cursor-pointer disabled:bg-medium disabled:cursor-not-allowed"
                    disabled={isQuizLive || loadingQuizId === quiz.id}
                    onClick={() => handleEditQuiz(quiz)}
                    style={{
                      backgroundColor: visualIdentity?.contrastColor || '#2356CC',
                    }}
                  >
                    <EditIcon
                      size={18}
                      color={isQuizLive || loadingQuizId === quiz.id ? '#C5C5CA' : '#FFFFFF'}
                    />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

      {/* Modal d'édition du quiz */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-4xl overflow-y-auto bg-medium max-h-[90vh]" hideCloseButton>
          <DialogHeader>
            <DialogTitle>{tWebinars('adminLive.editModal.quizTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-text-secondary">{tWebinars('adminLive.editModal.tools')}</p>
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={handleDeleteQuiz}
                className="bg-white rounded-lg py-3 px-2 border border-input cursor-pointer"
              >
                <Trash2 className="size-5" />
              </button>

              <Button
                variant="ghost"
                type="button"
                onClick={handleCloseModal}
                className=" cursor-pointer rounded-full hover:bg-transparent"
              >
                {tWebinars('adminLive.editModal.cancel')}
              </Button>

              <Button onClick={handleSaveFromModal} disabled={isSubmitting}>
                {isSubmitting ? <Loader /> : tWebinars('adminLive.editModal.save')}
              </Button>
            </div>
          </div>
          {selectedQuiz && (
            <QuizForm
              quizIndex={0}
              quizId={selectedQuiz.id.toString()}
              onRemove={() => {}}
              onRemoveAnswerOption={() => {}}
              canRemove={false}
              onSubmit={handleQuizSubmit}
              existingQuiz={selectedQuiz}
              isModal={true}
              onSave={handleSaveFromModal}
              onDelete={handleDeleteQuiz}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
